export interface Message {
    role: 'system' | 'user' | 'assistant';
    content: string;
}

export interface ModelPricing {
    input_price: number;
    output_price: number;
}

export interface PlaygroundParameters {
    maxTokens: number;
    temperature: number;
    topP: number;
    stoppingTokens: string[];
}

export interface ModelResponse {
    content: string;
    isStreaming: boolean;
    isComplete: boolean;
    error: string | null;
    tokenCount: {
        input: number;
        output: number;
    };
    latencyMs: number | null;
    startTime: number | null;
}

export type PlaygroundMode = 'compare-models' | 'compare-prompts';

export interface PlaygroundState {
    // Mode
    mode: PlaygroundMode;

    // Model selection
    availableModels: string[];
    modelPricing: Record<string, ModelPricing>;
    selectedModels: string[];

    // Messages
    // In compare-models mode: systemPrompts[0] and userMessages[0] are used
    // In compare-prompts mode: each index is a separate prompt with its own system
    systemPrompts: string[];
    userMessages: string[];

    // Shared parameters for model comparison; individual parameters for prompt comparison.
    promptParameters: PlaygroundParameters[];
    parameters: PlaygroundParameters;

    // Responses (keyed by `${promptIndex}-${modelId}`)
    responses: Record<string, ModelResponse>;

    // UI state
    isGenerating: boolean;
    isParametersPanelOpen: boolean;
}

// Helper functions for response keys
export const getResponseKey = (promptIndex: number, modelId: string) => `${promptIndex}-${modelId}`;
export const parseResponseKey = (key: string) => {
    const separatorIndex = key.indexOf('-');
    return {
        promptIndex: parseInt(key.substring(0, separatorIndex)),
        modelId: key.substring(separatorIndex + 1)
    };
};

export const MAX_USER_MESSAGES = 5;

export type PlaygroundAction =
    | { type: 'SET_MODE'; mode: PlaygroundMode }
    | { type: 'SET_AVAILABLE_MODELS'; models: string[]; pricing: Record<string, ModelPricing> }
    | { type: 'SET_SELECTED_MODELS'; models: string[] }
    | { type: 'ADD_MODEL'; modelId: string }
    | { type: 'REMOVE_MODEL'; modelId: string }
    | { type: 'SET_SYSTEM_PROMPT'; index: number; prompt: string }
    | { type: 'SET_USER_MESSAGE'; index: number; message: string }
    | { type: 'ADD_PROMPT' }
    | { type: 'REMOVE_PROMPT'; index: number }
    | { type: 'SET_PROMPTS'; systemPrompts: string[]; userMessages: string[] }
    | { type: 'SET_PROMPT_PARAMETERS'; index: number; params: Partial<PlaygroundParameters> }
    | { type: 'SET_PARAMETERS'; params: Partial<PlaygroundParameters> }
    | { type: 'START_GENERATION'; promptIndex: number; modelId: string }
    | { type: 'APPEND_CONTENT'; promptIndex: number; modelId: string; content: string }
    | { type: 'COMPLETE_GENERATION'; promptIndex: number; modelId: string; tokenCount?: { input: number; output: number } }
    | { type: 'GENERATION_ERROR'; promptIndex: number; modelId: string; error: string }
    | { type: 'LOAD_RESPONSE'; promptIndex: number; modelId: string; content: string; tokenCount: { input: number; output: number } }
    | { type: 'CLEAR_RESPONSES' }
    | { type: 'RESET_ALL' }
    | { type: 'SET_IS_GENERATING'; isGenerating: boolean }
    | { type: 'TOGGLE_PARAMETERS_PANEL' }
    | { type: 'LOAD_SESSION'; session: {
        mode: PlaygroundMode;
        selectedModels: string[];
        systemPrompts: string[];
        userMessages: string[];
        parameters: PlaygroundParameters;
        promptParameters?: PlaygroundParameters[];
        responses: Record<string, { content: string; tokenCount: { input: number; output: number } }>;
    }};

export interface PlaygroundContextType {
    state: PlaygroundState;
    dispatch: React.Dispatch<PlaygroundAction>;

    // Convenience actions
    setMode: (mode: PlaygroundMode) => void;
    setSelectedModels: (models: string[]) => void;
    addModel: (modelId: string) => void;
    removeModel: (modelId: string) => void;
    setSystemPrompt: (index: number, prompt: string) => void;
    setUserMessage: (index: number, message: string) => void;
    addPrompt: () => void;
    removePrompt: (index: number) => void;
    setPrompts: (systemPrompts: string[], userMessages: string[]) => void;
    setPromptParameters: (index: number, params: Partial<PlaygroundParameters>) => void;
    setParameters: (params: Partial<PlaygroundParameters>) => void;
    clearResponses: () => void;
    resetAll: () => void;
    toggleParametersPanel: () => void;
}

export const DEFAULT_PARAMETERS: PlaygroundParameters = {
    maxTokens: 1024,
    temperature: 0.7,
    topP: 0.95,
    stoppingTokens: [],
};

export const INITIAL_STATE: PlaygroundState = {
    mode: 'compare-models',
    availableModels: [],
    modelPricing: {},
    selectedModels: [],
    systemPrompts: [''],
    userMessages: [''],
    parameters: DEFAULT_PARAMETERS,
    promptParameters: [],
    responses: {},
    isGenerating: false,
    isParametersPanelOpen: false,
};

export const MAX_SELECTED_MODELS = Infinity;

// Saved session types (from backend API)
export interface SavedSession {
    id: string;
    name: string;
    created_at: number;
    updated_at: number;
    mode: PlaygroundMode;
    selected_models: string[];
    system_prompts: string[];
    user_messages: string[];
    parameters: {
        max_tokens: number;
        temperature: number;
        top_p: number;
        prompt_parameters?: { max_tokens: number; temperature: number; top_p: number }[];
    };
    responses?: Record<string, {
        content: string;
        token_count: { input: number; output: number };
    }>;
}
